import { ClientCombobox } from "@/components/ClientCombobox";
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, euro, downloadReport } from "@/lib/api";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CalculatorIcon as Calculator, FileCsvIcon as FileCsv, FileXlsIcon as FileXls, FilePdfIcon as FilePdf, FloppyDiskIcon as FloppyDisk } from "@phosphor-icons/react";

function periodPreset(type) {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), 1);
  let start;
  if (type === "mensile") start = new Date(end.getFullYear(), end.getMonth() - 1, 1);
  else if (type === "trimestrale") start = new Date(end.getFullYear(), end.getMonth() - 3, 1);
  else start = new Date(end.getFullYear() - 1, end.getMonth(), 1);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { start: iso(start), end: iso(end) };
}

export default function Billing() {
  const [clients, setClients] = useState([]);
  const [cliente, setCliente] = useState("");
  const [periodType, setPeriodType] = useState("mensile");
  const [start, setStart] = useState(periodPreset("mensile").start);
  const [end, setEnd] = useState(periodPreset("mensile").end);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(null);

  useEffect(() => { api.get("/clients").then(({ data }) => setClients(data)); }, []);

  const onPeriodType = (v) => {
    setPeriodType(v);
    const p = periodPreset(v);
    setStart(p.start); setEnd(p.end);
  };

  const calculate = async () => {
    if (!cliente) { toast.error("Seleziona un cliente"); return; }
    setLoading(true); setGenerated(null);
    try {
      const { data } = await api.post("/billing/calculate", {
        cliente_id: cliente, period_start: start, period_end: end, period_type: periodType,
      });
      setResult(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/reports", {
        cliente_id: cliente, period_start: start, period_end: end, period_type: periodType,
      });
      setGenerated(data);
      toast.success("Report generato e archiviato", { description: "Disponibile in CSV, Excel e PDF." });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <InternalLayout>
      <div className="p-8 lg:p-12">
        <PageHeader title="Fatturazione ricorsiva" subtitle="Calcolo canoni + consumi e generazione report per la fatturazione esterna" />

        <Card className="rounded-sm border-border shadow-none p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider">Cliente</Label>
              <div className="mt-1"><ClientCombobox clients={clients} value={cliente} onChange={setCliente} placeholder="Seleziona cliente" testid="billing-cliente" /></div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Periodicità</Label>
              <Select value={periodType} onValueChange={onPeriodType}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="billing-period-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mensile">Mensile</SelectItem>
                  <SelectItem value="trimestrale">Trimestrale</SelectItem>
                  <SelectItem value="annuale">Annuale</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Dal</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 rounded-sm" data-testid="billing-start" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Al</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 rounded-sm" data-testid="billing-end" />
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={calculate} disabled={loading} data-testid="calculate-btn" className="rounded-sm gap-2">
              <Calculator size={16} weight="bold" /> Calcola importi
            </Button>
          </div>
        </Card>

        {result && (
          <Card className="rounded-sm border-border shadow-none">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h3 className="font-heading text-lg font-semibold">{result.cliente}</h3>
                <p className="text-sm text-muted-foreground">
                  Periodo {result.period_start} → {result.period_end} · {result.period_type}
                </p>
              </div>
              <Button onClick={generate} disabled={loading || result.righe.length === 0} data-testid="generate-report-btn" className="rounded-sm gap-2">
                <FloppyDisk size={16} weight="bold" /> Genera report
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Contratto</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead className="text-right">Q.tà</TableHead>
                  <TableHead className="text-right">Prezzo unit.</TableHead>
                  <TableHead className="text-right">Importo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.righe.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Nessun importo per questo periodo. Verifica contratti e letture.</TableCell></TableRow>}
                {result.righe.map((r, i) => (
                  <TableRow key={i} data-testid="billing-row">
                    <TableCell><Badge variant="outline" className="rounded-sm">{r.tipo}</Badge></TableCell>
                    <TableCell className="text-muted-foreground">{r.contratto}</TableCell>
                    <TableCell className="text-sm">{r.descrizione}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.quantita}</TableCell>
                    <TableCell className="text-right tabular-nums">{euro(r.prezzo_unitario)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{euro(r.importo)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between p-6 border-t border-border bg-secondary">
              <span className="text-sm uppercase tracking-wider font-semibold text-muted-foreground">Totale periodo</span>
              <span className="font-heading text-2xl font-bold tabular-nums" data-testid="billing-total">{euro(result.totale)}</span>
            </div>
          </Card>
        )}

        {generated && (
          <Card className="rounded-sm border-primary/30 border shadow-none p-6 mt-6 bg-blue-50/40" data-testid="generated-report">
            <h3 className="font-heading text-lg font-semibold mb-1">Report archiviato</h3>
            <p className="text-sm text-muted-foreground mb-4">Scarica nei formati pronti per il software di fatturazione esterno.</p>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" className="rounded-sm gap-2" onClick={() => downloadReport(generated.id, "csv", `report_${generated.cliente_nome}`)}><FileCsv size={18} /> CSV</Button>
              <Button variant="outline" className="rounded-sm gap-2" onClick={() => downloadReport(generated.id, "xlsx", `report_${generated.cliente_nome}`)}><FileXls size={18} /> Excel</Button>
              <Button variant="outline" className="rounded-sm gap-2" onClick={() => downloadReport(generated.id, "pdf", `report_${generated.cliente_nome}`)}><FilePdf size={18} /> PDF</Button>
            </div>
          </Card>
        )}
      </div>
    </InternalLayout>
  );
}
